import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  fetchkeypickupServiceRequests,
  fetchkeypickupImportantDocuments,
} from 'src/services/client';
import {
  useSuiteUsersListOperations,
  UseSuiteUsersListOperationsReturn,
} from 'src/pages/SharedPageCustomHooks/UseContactSaveSuitesPage/useSuiteUsersListOperations';
import { SuiteUserRow } from 'src/pages/SharedPageCustomHooks/UseContactSaveSuitesPage/types';

export interface KeyPickupServiceRequest {
  id: string;
  [key: string]: any;
}

export interface KeyPickupImportantDocument {
  id: string;
  [key: string]: any;
}

export type SuiteUser = SuiteUserRow;

export interface UseKeyPickupDataReturn {
  serviceRequests: KeyPickupServiceRequest[];
  importantDocuments: KeyPickupImportantDocument[];
  suiteUsers: SuiteUser[];
  isLoading: boolean;
  isFetchingSuiteUsers: boolean;
  refetch: () => Promise<void>;
  fetchSuiteUsersData: () => Promise<void>;
  resetState: () => void;
}

/* Custom hook for fetching key pickup service requests, important documents, and
 * suite users. Provides loading states, error handling, & refetch functionality */
export const useKeyPickupData = (suiteId: string): UseKeyPickupDataReturn => {
  const [serviceRequests, setServiceRequests] = useState<
    KeyPickupServiceRequest[]
  >([]);
  const [importantDocuments, setImportantDocuments] = useState<
    KeyPickupImportantDocument[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    suiteUsers,
    isFetchingSuiteUsers,
    fetchSuiteUsers,
  }: UseSuiteUsersListOperationsReturn = useSuiteUsersListOperations();

  const fetchSuiteUsersData = useCallback(async (): Promise<void> => {
    if (!suiteId) {
      return;
    }

    await fetchSuiteUsers(suiteId);
  }, [suiteId, fetchSuiteUsers]);

  const fetchData = useCallback(async () => {
    if (!suiteId) return;

    try {
      setIsLoading(true);

      const serviceRequestsResponse: any =
        await fetchkeypickupServiceRequests(suiteId);
      const importantDocumentsResponse: any =
        await fetchkeypickupImportantDocuments(suiteId);

      let serviceRequestsData: KeyPickupServiceRequest[] = [];

      if (serviceRequestsResponse.status === 200) {
        const serviceRequestsJson = await serviceRequestsResponse.json();
        serviceRequestsData = serviceRequestsJson || [];
      }

      let importantDocumentsData: KeyPickupImportantDocument[] = [];

      if (importantDocumentsResponse.status === 200) {
        const importantDocumentsJson = await importantDocumentsResponse.json();
        importantDocumentsData = importantDocumentsJson || [];
      }

      setServiceRequests(serviceRequestsData);
      setImportantDocuments(importantDocumentsData);
    } catch (error: any) {
      console.error('Error fetching key pickup data:', error);

      toast.error(`🚫 ${error.message || 'Failed to fetch key pickup data'}`, {
        autoClose: 10000,
        position: 'top-center',
      });

      setServiceRequests([]);
      setImportantDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [suiteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetState = useCallback(() => {
    setServiceRequests([]);
    setImportantDocuments([]);
    setIsLoading(false);
  }, []);

  return {
    serviceRequests,
    importantDocuments,
    suiteUsers,
    isLoading,
    isFetchingSuiteUsers,
    refetch: fetchData,
    fetchSuiteUsersData,
    resetState,
  };
};
